import { useEffect, type DragEvent, type ReactNode } from "react";
import { DotsSixVertical } from "@/components/ui/icons";
import { emitTerminalTelemetry } from "@/lib/terminal-telemetry";
import type { CardDensity } from "./card-utils";

interface CardShellProps {
  compact?: boolean;
  density?: CardDensity;
  draggable?: boolean;
  onDragStart?: (event: DragEvent<HTMLDivElement>) => void;
  icon: ReactNode;
  label: ReactNode;
  labelClassName?: string;
  /** When false, the label is rendered as-is (no uppercase). Defaults to true. */
  labelUppercase?: boolean;
  headerActions?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  rootClassName?: string;
  telemetryCardType?: string;
}

function rootClasses(
  compact?: boolean,
  density?: CardDensity,
  draggable?: boolean,
): string {
  if (compact) {
    const drag = draggable ? " cursor-grab active:cursor-grabbing" : "";
    return `w-full max-w-[34rem] rounded-lg overflow-hidden transition-colors my-2 text-[13px] bg-card shadow-sm shadow-black/20${drag}`;
  }

  if (density === "ultra-compact") {
    return "w-full max-w-[34rem] rounded-lg overflow-hidden transition-colors font-terminal text-[10px] my-0.5 bg-gradient-to-b from-card to-background cursor-grab active:cursor-grabbing shadow-[0_1px_0_rgba(255,255,255,0.03)_inset] group";
  }

  if (density === "compact") {
    return "w-full max-w-[34rem] rounded-lg overflow-hidden transition-colors font-terminal text-[10px] my-1 bg-gradient-to-b from-card to-background cursor-grab active:cursor-grabbing shadow-[0_1px_0_rgba(255,255,255,0.03)_inset] group";
  }

  return "w-full max-w-[34rem] rounded-lg overflow-hidden transition-colors font-terminal text-[11px] my-1.5 bg-gradient-to-b from-card to-background cursor-grab active:cursor-grabbing shadow-[0_1px_0_rgba(255,255,255,0.03)_inset] group";
}

function headerClasses(compact?: boolean, density?: CardDensity): string {
  return `flex items-center gap-1.5 bg-background border-b border-border ${
    compact || density !== "comfortable" ? "px-2 py-1" : "px-2.5 py-1.5"
  }`;
}

export function CardShell({
  compact,
  density,
  draggable,
  onDragStart,
  icon,
  label,
  labelClassName,
  labelUppercase = true,
  headerActions,
  children,
  contentClassName,
  rootClassName,
  telemetryCardType,
}: CardShellProps) {
  useEffect(() => {
    if (!telemetryCardType) return;
    emitTerminalTelemetry("agent_card_rendered", {
      card_type: telemetryCardType,
      compact: Boolean(compact),
    });
  }, [compact, telemetryCardType]);

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      className={`${rootClasses(compact, density, draggable)}${rootClassName ? ` ${rootClassName}` : ""}`}
    >
      <div className={headerClasses(compact, density)}>
        {!compact && (
          <DotsSixVertical
            size={10}
            className="text-muted-foreground group-hover:text-muted-foreground"
          />
        )}
        {icon}
        <span
          className={`text-[10px] ${labelUppercase ? "uppercase tracking-wider" : "tracking-wide"} ${compact ? "font-bold" : "font-terminal font-bold"} ${labelClassName ?? "text-muted-foreground"}`}
        >
          {label}
        </span>
        {headerActions ? <div className="ml-auto">{headerActions}</div> : null}
      </div>

      <div className={contentClassName}>{children}</div>
    </div>
  );
}
